/**
 * VM Pool Management Page
 */

import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { PlusIcon, TrashIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import Header from '../components/layout/Header';
import { useVMPools } from '../hooks/useVMPools';
import { useProjects } from '../hooks/useProjects';
import { VMPool } from '../types/vmPool';
import { Project } from '../types';
import { useSelectedProject } from '../hooks/useSelectedProject';

const VMPools: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const projectFromUrl = searchParams.get('project');
  const pageFromUrl = searchParams.get('page');

  const {
    selectedProjectId: globalProjectId,
    projects,
    setSelectedProject: setGlobalProject,
    autoSelectFirstProject,
  } = useSelectedProject();

  // Use URL param if available, otherwise use global selection
  const selectedProject = projectFromUrl || globalProjectId;

  const [page, setPage] = useState<number>(pageFromUrl ? parseInt(pageFromUrl, 10) : 1);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);

  // Auto-select first project if none selected and no URL param
  useEffect(() => {
    if (!projectFromUrl) {
      autoSelectFirstProject();
    }
  }, [projectFromUrl, autoSelectFirstProject]);

  useEffect(() => {
    if (pageFromUrl) {
      const pageNum = parseInt(pageFromUrl, 10);
      if (!isNaN(pageNum) && pageNum > 0) {
        setPage(pageNum);
      }
    }
  }, [pageFromUrl]);

  const projectId = selectedProject || undefined;

  useEffect(() => {
    const params = new URLSearchParams();
    if (selectedProject) {
      params.set('project', selectedProject);
    }
    if (page > 1) {
      params.set('page', page.toString());
    }
    const newParamsString = params.toString();
    const currentParamsString = searchParams.toString();
    if (newParamsString !== currentParamsString) {
      setSearchParams(params, { replace: true });
    }
  }, [selectedProject, page, setSearchParams, searchParams]);

  const {
    pools,
    totalCount,
    page: currentPage,
    pages,
    isLoading,
    isError,
    refetch,
    createPool,
    isCreating,
    scalePool,
    isScaling,
    deletePool,
    isDeleting,
  } = useVMPools({
    projectId,
    page,
  });

  useEffect(() => {
    const interval = setInterval(() => {
      refetch();
    }, 30000);
    return () => clearInterval(interval);
  }, [refetch]);

  const handleCreatePool = () => {
    if (projectId) {
      navigate(`/vm-pools/create?project=${projectId}`);
    } else {
      navigate('/vm-pools/create');
    }
  };

  const handleScalePool = (poolId: string, currentReplicas: number) => {
    const newReplicas = prompt(`Current replicas: ${currentReplicas}\nEnter new number of replicas:`, currentReplicas.toString());
    if (newReplicas) {
      const replicas = parseInt(newReplicas, 10);
      if (!isNaN(replicas) && replicas >= 0) {
        scalePool({ poolId, replicas });
      } else {
        toast.error('Please enter a valid number');
      }
    }
  };

  const handleDeletePool = (poolId: string) => {
    setShowDeleteConfirm(poolId);
  };

  const confirmDelete = () => {
    if (showDeleteConfirm) {
      deletePool(showDeleteConfirm);
      setShowDeleteConfirm(null);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'running':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'creating':
      case 'scaling':
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'stopped':
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
      case 'error':
        return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'deleting':
        return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
      default:
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
    }
  };

  return (
    <div>
      <Header
        title="VM Pools"
        subtitle="Manage groups of virtual machines"
        actions={
          <button
            onClick={handleCreatePool}
            className="btn-primary flex items-center gap-2"
            disabled={isCreating}
          >
            <PlusIcon className="w-4 h-4" />
            Create Pool
          </button>
        }
      />

      <div className="p-6">
        {/* Stats Card */}
        <div className="bg-gray-900 rounded-xl shadow-sm border border-gray-800 p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-400">Total Pools</p>
              <p className="mt-2 text-3xl font-bold text-white">{totalCount}</p>
            </div>
            <div className="bg-primary-500/20 p-3 rounded-xl">
              <svg className="w-6 h-6 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-gray-900 rounded-xl shadow-sm border border-gray-800 p-4 mb-6">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-300 whitespace-nowrap">Project:</label>
              <select
                value={selectedProject}
                onChange={(e) => {
                  const newProject = e.target.value;
                  setGlobalProject(newProject);
                  setPage(1);
                }}
                className="px-3 py-2 border border-gray-600 rounded-md text-sm bg-gray-800 text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 min-w-[200px] transition-colors"
                aria-label="Filter by project"
              >
                <option value="">All Projects</option>
                {projects.map((project: Project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Pools Table */}
        {isLoading ? (
          <div className="bg-gray-900 rounded-xl shadow-sm border border-gray-800 p-12">
            <div className="flex flex-col items-center justify-center">
              <div className="w-8 h-8 border-4 border-gray-700 border-t-primary-500 rounded-full animate-spin mb-4"></div>
              <p className="text-sm text-gray-400">Loading VM pools...</p>
            </div>
          </div>
        ) : isError ? (
          <div className="bg-gray-900 rounded-xl shadow-sm border border-gray-800 p-12">
            <div className="text-center">
              <p className="text-red-400 mb-4">Failed to load VM pools</p>
              <button onClick={() => refetch()} className="btn-primary">
                Retry
              </button>
            </div>
          </div>
        ) : pools.length === 0 ? (
          <div className="bg-gray-900 rounded-xl shadow-sm border border-gray-800 p-12">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-gray-800 mb-4">
                <svg className="h-8 w-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-white">No VM Pools</h3>
              <p className="mt-2 text-sm text-gray-400 max-w-md mx-auto">
                {selectedProject
                  ? "You don't have any VM pools in this project yet. Create your first VM pool to get started."
                  : "Select a project above or create a new VM pool to get started."}
              </p>
              <div className="mt-8">
                <button
                  onClick={handleCreatePool}
                  className="btn-primary inline-flex items-center gap-2"
                >
                  <PlusIcon className="w-5 h-5" />
                  Create VM Pool
                </button>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="bg-gray-900 rounded-xl shadow-sm border border-gray-800 overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-800 border-b border-gray-700">
                  <tr>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Name</th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Status</th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Replicas</th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">VMs</th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Created</th>
                    <th className="text-right px-6 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {pools.map((pool: VMPool) => (
                    <tr key={pool.id} className="hover:bg-gray-800">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-white">{pool.name}</div>
                          {pool.description && (
                            <div className="text-sm text-gray-400">{pool.description}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(pool.status)}`}>
                          {pool.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                        {pool.replicas}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                        {pool.members?.length || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                        {new Date(pool.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleScalePool(pool.id, pool.replicas)}
                            disabled={isScaling || pool.status === 'deleting'}
                            className="text-primary-400 hover:text-primary-300 disabled:opacity-50"
                            title="Scale Pool"
                          >
                            <ArrowPathIcon className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleDeletePool(pool.id)}
                            disabled={isDeleting || pool.status === 'deleting'}
                            className="text-red-400 hover:text-red-300 disabled:opacity-50"
                            title="Delete Pool"
                          >
                            <TrashIcon className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pages > 1 && (
              <div className="mt-6 flex items-center justify-between">
                <div className="text-sm text-gray-400">
                  Showing page {currentPage} of {pages}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-4 py-2 border border-gray-600 rounded-md text-sm font-medium text-gray-300 bg-gray-800 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage((p) => Math.min(pages, p + 1))}
                    disabled={page >= pages}
                    className="px-4 py-2 border border-gray-600 rounded-md text-sm font-medium text-gray-300 bg-gray-800 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/70" onClick={() => setShowDeleteConfirm(null)} />
          <div className="relative bg-gray-900 border border-gray-700 rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Delete VM Pool</h3>
            <p className="text-gray-400 mb-6">
              Are you sure you want to delete this VM pool? This will delete all VMs in the pool.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="px-4 py-2 border border-gray-600 text-gray-300 rounded-lg hover:bg-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={isDeleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VMPools;
